/**
 * Keyboard bindings by physical key (KeyboardEvent.code), so they work on every layout:
 * Russian, Greek, AZERTY… (docs/fight.md §9). Remapping in settings later only has to swap
 * this object.
 */
import type { BUTTON } from '@caged/shared';

export type ButtonName = keyof typeof BUTTON;
export type Direction = 'up' | 'down' | 'left' | 'right';

/** Movement key bits (ControlsView.dirs). */
export const DIR: Readonly<Record<Direction, number>> = { up: 1, down: 2, left: 4, right: 8 };

export interface KeyBindings {
  move: Readonly<Record<Direction, readonly string[]>>;
  buttons: Readonly<Record<ButtonName, readonly string[]>>;
  /** Offline fights only. */
  pause: readonly string[];
  /** Dev builds only. */
  tuning: readonly string[];
}

export const DEFAULT_BINDINGS: KeyBindings = {
  move: {
    up: ['KeyW', 'ArrowUp'],
    down: ['KeyS', 'ArrowDown'],
    left: ['KeyA', 'ArrowLeft'],
    right: ['KeyD', 'ArrowRight'],
  },
  buttons: {
    strike: ['KeyJ'],
    grapple: ['KeyK'],
    defend: ['KeyL'],
    signature: ['Space'],
  },
  pause: ['Escape'],
  tuning: ['Tab'],
};

/** Keys whose browser default (page scroll, focus moves) is blocked while fighting. */
const BLOCKED_DEFAULTS = new Set([
  'Space',
  'Tab',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
]);

export function blocksDefault(code: string): boolean {
  return BLOCKED_DEFAULTS.has(code);
}

/** Text fields and other controls that need the keyboard for themselves. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/** Focused buttons and links, which Space and Enter activate (menus work with the keyboard). */
export function isActivationTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  if (target.closest('[data-cg-button]')) return false;
  return target.closest('button, a[href], summary, [role="button"], [role="menuitem"]') !== null;
}
